import { Injectable } from '@angular/core';
import { BehaviorSubject, timer, Observable, NEVER } from 'rxjs';
import { iJWT } from 'src/app/interfaces/jwt';

@Injectable({
  providedIn: 'root'
})
export class JwtService {
  private store: tJWT[] = [];

  constructor() { }

  public async addJWT(jwt: string | iJWT, notifyBefore?: {num: number, type: "%" | "m" | "s"}, token?: string): Promise<number> {
    if (!jwt)
      return Promise.reject("JWT wasn't supplied");

    const JWT = await this.parse(jwt);

    let expire = Infinity;
    if (JWT.exp) {
      expire = JWT.exp * 1000;
      if (notifyBefore) {
        if (notifyBefore.type == "%")
          expire = Date.now() + ((expire - Date.now()) * (1 - (notifyBefore.num / 100)));
        else
          expire = expire - notifyBefore.num * (notifyBefore.type == "m" ? 60 * 1000 : 1000);
      }
    }

    const obj: tJWT = {
      ...((typeof jwt == "string" && {jwt}) || (token && {token}) || {}),
      valid: () => expire == Infinity || (new Date().getTime()) < (new Date(expire).getTime()),
      expired: () => !JWT.exp ? false : (new Date().getTime()) >= (new Date(JWT.exp * 1000).getTime()) ,
      expire: (expire == Infinity ? NEVER : timer(new Date(expire))),
      parsed: JWT,
    };

    return this.store.push(obj) - 1;
  }

  public getJWT<T = any>(key: number): tJWT<T> {
    return this.store[key];
  }

  public parseJSON<T = any>(json: string): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      let obj: T;
      try {
        obj = JSON.parse(json);
      } catch (e) {
        return reject(e);
      }
      return resolve(obj);
    });
  }

  public b64DecodeUnicode(str: string): string {
    return decodeURIComponent(atob(str).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
  }

  public async parse(token: string | iJWT): Promise<iJWT> {
    if (typeof token !== "string")
      return token;

    const base64Url = token.split('.');
    if (base64Url.length != 3)
      throw new Error("String is not a valid JWT");

    const base64 = base64Url[1].replace(/-/g, '+').replace(/_/g, '/');
    return this.parseJSON<iJWT>(this.b64DecodeUnicode(base64));
  }
}

export interface tJWT<T = any> {
  jwt?: string;
  parsed: iJWT & T;
  expire?: Observable<number>;
  valid: () => boolean;
  expired: () => boolean;
}